import React, { useState, useEffect } from 'react';
import { institutionAPI } from '../../lib/api';
import { 
  Users, 
  UserPlus, 
  AlertTriangle, 
  AlertCircle,
  Info,
  TrendingUp,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { logger } from '../../lib/logger';

export default function UsageLimitsCard() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const res = await institutionAPI.getUsage();
        setUsage(res.data);
      } catch (error) {
        logger.error('Failed to fetch usage:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsage();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-slate-200 rounded w-1/3"></div>
            <div className="h-8 bg-slate-200 rounded"></div>
            <div className="h-8 bg-slate-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!usage) return null;

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 90) return 'bg-amber-500';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-emerald-500';
  };

  const getWarningIcon = (level) => {
    switch (level) {
      case 'error': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'info': return <Info className="w-4 h-4 text-blue-500" />;
      default: return null;
    }
  };

  return (
    <Card data-testid="usage-limits-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-cyan-600" />
          Usage & Limits
        </CardTitle>
        <CardDescription>
          Current plan: <span className="font-semibold text-slate-700">{usage.plan_name}</span> • {usage.month}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Warnings */}
        {usage.warnings && usage.warnings.length > 0 && (
          <div className="space-y-2">
            {usage.warnings.map((warning, idx) => (
              <div 
                key={`${warning.type}-${warning.level}`}
                className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                  warning.level === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
                  warning.level === 'warning' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                  'bg-blue-50 text-blue-700 border border-blue-200'
                }`}
              >
                {getWarningIcon(warning.level)}
                <span>{warning.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* Users Usage */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-slate-600">
              <Users className="w-4 h-4" />
              <span>Active Users</span>
            </div>
            <span className="font-medium">
              {usage.users.unlimited ? (
                <span className="text-emerald-600">Unlimited</span>
              ) : (
                <span className={usage.users.percentage >= 100 ? 'text-red-600' : ''}>
                  {usage.users.current} / {usage.users.limit}
                </span>
              )}
            </span>
          </div>
          {!usage.users.unlimited && (
            <div className="relative">
              <Progress 
                value={Math.min(usage.users.percentage, 100)} 
                className="h-2"
              />
              <div 
                className={`absolute top-0 left-0 h-full rounded-full transition-all ${getProgressColor(usage.users.percentage)}`}
                style={{ width: `${Math.min(usage.users.percentage, 100)}%` }}
              />
            </div>
          )}
          {!usage.users.unlimited && usage.users.remaining !== null && (
            <p className="text-xs text-slate-500">
              {usage.users.remaining > 0 
                ? `${usage.users.remaining} user slots remaining`
                : 'No user slots remaining - upgrade to add more users'}
            </p>
          )}
        </div>

        {/* Patients Usage */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-slate-600">
              <UserPlus className="w-4 h-4" />
              <span>Patients This Month</span>
            </div>
            <span className="font-medium">
              {usage.patients_this_month.unlimited ? (
                <span className="text-emerald-600">Unlimited</span>
              ) : (
                <span className={usage.patients_this_month.percentage >= 100 ? 'text-red-600' : ''}>
                  {usage.patients_this_month.current} / {usage.patients_this_month.limit}
                </span>
              )}
            </span>
          </div>
          {!usage.patients_this_month.unlimited && (
            <div className="relative">
              <Progress 
                value={Math.min(usage.patients_this_month.percentage, 100)} 
                className="h-2"
              />
              <div 
                className={`absolute top-0 left-0 h-full rounded-full transition-all ${getProgressColor(usage.patients_this_month.percentage)}`}
                style={{ width: `${Math.min(usage.patients_this_month.percentage, 100)}%` }}
              />
            </div>
          )}
          {!usage.patients_this_month.unlimited && usage.patients_this_month.remaining !== null && (
            <p className="text-xs text-slate-500">
              {usage.patients_this_month.remaining > 0 
                ? `${usage.patients_this_month.remaining} patient registrations remaining this month`
                : 'Monthly limit reached - upgrade or wait for next month'}
            </p>
          )}
        </div>

        {/* Upgrade CTA */}
        {(usage.users.percentage >= 80 || usage.patients_this_month.percentage >= 80) && (
          <div className="pt-2 border-t">
            <Button 
              onClick={() => navigate('/subscription')}
              className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
              data-testid="upgrade-plan-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              Upgrade Plan for Higher Limits
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
